import {
	DeleteOutlined,
	EditOutlined,
	FolderOpenOutlined,
	PlusOutlined,
	UserAddOutlined,
} from "@ant-design/icons";
import { Button, Empty, Popconfirm, Table, Tabs, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { Link } from "react-router-dom";
import { TerritoryAssignStaffModal } from "@/modules/territories/components/TerritoryAssignStaffModal";
import { TerritoryFormModal } from "@/modules/territories/components/TerritoryFormModal";
import { TerritoryImportModal } from "@/modules/territories/components/TerritoryImportModal";
import { TerritoryLiveMap } from "@/modules/territories/components/TerritoryLiveMap";
import { useDeleteTerritory, useTerritories } from "@/modules/territories/hooks/useTerritories";
import type { Territory } from "@/modules/territories/services/territory.service";
import { useUrlTableState } from "@/shared/hooks/useUrlState";

interface OrgTerritoriesTabProps {
	organizationId: string;
}

export function OrgTerritoriesTab({ organizationId }: OrgTerritoriesTabProps) {
	const { page, limit, setPage } = useUrlTableState({ prefix: "terr" });
	const [parentStack, setParentStack] = useState<Array<{ id: string; name: string }>>([]);
	const [assignStaffTerritory, setAssignStaffTerritory] = useState<Territory | null>(null);
	const [isCreateOpen, setIsCreateOpen] = useState(false);
	const [editingTerritory, setEditingTerritory] = useState<Territory | null>(null);
	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
	const currentParentId = parentStack.at(-1)?.id;

	const { mutate: deleteTerritory, isPending: isDeleting } = useDeleteTerritory();

	const { data, isLoading } = useTerritories({
		organizationId,
		page,
		limit,
		parentId: currentParentId,
	});

	// Drill-down keeps the parent stack local (it needs names for breadcrumbs);
	// only reset the URL page when it actually moved off the default.
	const resetPage = () => {
		if (page !== 1) {
			setPage(1);
		}
	};

	const goRoot = () => {
		setParentStack([]);
		resetPage();
	};

	const goBack = () => {
		setParentStack((prev) => prev.slice(0, -1));
		resetPage();
	};

	const openChildren = (record: Territory) => {
		setParentStack((prev) => [...prev, { id: record.id, name: record.name }]);
		resetPage();
	};

	const columns: ColumnsType<Territory> = [
		{
			title: "Nomi",
			dataIndex: "name",
			key: "name",
			ellipsis: true,
			render: (name: string, record: Territory) => (
				<Link to={`/territories/${record.id}`}>{name}</Link>
			),
		},
		{
			title: "Yuzasi",
			dataIndex: "area",
			key: "area",
			width: 120,
			render: (area: number) => (area == null ? "—" : area.toLocaleString("uz-UZ")),
		},
		{
			title: "Perimetr",
			dataIndex: "perimeter",
			key: "perimeter",
			width: 120,
			render: (perimeter: number) => (perimeter == null ? "—" : perimeter.toLocaleString("uz-UZ")),
		},
		{
			title: "Brigadalar",
			dataIndex: "assignedBrigades",
			key: "assignedBrigades",
			ellipsis: true,
			render: (brigades?: Territory["assignedBrigades"]) =>
				brigades && brigades.length > 0 ? brigades.map((b) => b.name).join(", ") : "—",
		},
		{
			title: "Xodimlar",
			dataIndex: "assignedStaffCount",
			key: "assignedStaffCount",
			width: 100,
			render: (count: number) => count ?? 0,
		},
		{
			title: "Vazifalar",
			dataIndex: "taskCount",
			key: "taskCount",
			width: 100,
			render: (count: number) => count ?? 0,
		},
		{
			title: "Ekinlar",
			dataIndex: "plantingCount",
			key: "plantingCount",
			width: 100,
			render: (count: number) => count ?? 0,
		},
		{
			title: "Yaratilgan",
			dataIndex: "createdAt",
			key: "createdAt",
			width: 150,
			render: (date: string) =>
				new Date(date).toLocaleDateString("uz-UZ", {
					year: "numeric",
					month: "2-digit",
					day: "2-digit",
					hour: "2-digit",
					minute: "2-digit",
				}),
		},
		{
			title: "Amallar",
			key: "actions",
			width: 140,
			fixed: "right",
			render: (_: unknown, record: Territory) => (
				<div className="flex items-center gap-1">
					<Tooltip title="Ichki hududlar">
						<Button
							type="text"
							size="small"
							icon={<FolderOpenOutlined />}
							onClick={() => {
								openChildren(record);
							}}
						/>
					</Tooltip>
					<Tooltip title="Xodim biriktirish">
						<Button
							type="text"
							size="small"
							icon={<UserAddOutlined />}
							onClick={() => {
								setAssignStaffTerritory(record);
							}}
						/>
					</Tooltip>
					<Tooltip title="Tahrirlash">
						<Button
							type="text"
							size="small"
							icon={<EditOutlined />}
							onClick={() => {
								setEditingTerritory(record);
							}}
						/>
					</Tooltip>
					<Popconfirm
						title="Hududni o'chirish"
						description="Haqiqatan ham ushbu hududni o'chirmoqchimisiz?"
						onConfirm={() => deleteTerritory(record.id)}
						okText="O'chirish"
						cancelText="Bekor qilish"
						okButtonProps={{ danger: true, loading: isDeleting }}
					>
						<Tooltip title="O'chirish">
							<Button type="text" size="small" danger icon={<DeleteOutlined />} />
						</Tooltip>
					</Popconfirm>
				</div>
			),
		},
	];

	const tabItems = [
		{
			key: "list",
			label: "Ro'yxat",
			children: (
				<div className="space-y-3">
					<div className="flex items-center justify-between gap-2">
						<div className="flex items-center gap-2">
							<Button size="small" onClick={goRoot} disabled={parentStack.length === 0}>
								Asosiy hududlar
							</Button>
							<Button size="small" onClick={goBack} disabled={parentStack.length === 0}>
								Orqaga
							</Button>
							<div className="text-xs text-gray-500">
								{parentStack.length === 0
									? "Daraja: Asosiy"
									: `Daraja: ${parentStack.map((item) => item.name).join(" / ")}`}
							</div>
							{selectedRowKeys.length > 0 && (
								<div className="text-xs text-blue-600 font-medium ml-2">
									Tanlangan: {selectedRowKeys.length} ta
								</div>
							)}
						</div>
						<Button
							type="primary"
							icon={<PlusOutlined />}
							size="small"
							onClick={() => setIsCreateOpen(true)}
						>
							Yangi hudud
						</Button>
					</div>

					<Table
						columns={columns}
						dataSource={data?.data?.items}
						loading={isLoading}
						rowKey="id"
						scroll={{ x: "max-content" }}
						rowSelection={{
							selectedRowKeys,
							onChange: setSelectedRowKeys,
						}}
						pagination={{
							current: page,
							pageSize: limit,
							total: data?.data?.meta?.total,
							onChange: setPage,
							showSizeChanger: false,
							showTotal: (total) => `Jami: ${total}`,
						}}
						size="middle"
						locale={{
							emptyText: (
								<Empty
									image={Empty.PRESENTED_IMAGE_SIMPLE}
									description="Hududlar mavjud emas. Yangi hudud qo'shish yoki KMZ fayldan import qilish mumkin."
								/>
							),
						}}
					/>
				</div>
			),
		},
		{
			key: "map",
			label: "Xarita (Live Tracking)",
			children: <TerritoryLiveMap organizationId={organizationId} />,
		},
	];

	return (
		<>
			<Tabs items={tabItems} />
			<TerritoryImportModal organizationId={organizationId} />
			<TerritoryAssignStaffModal
				territory={assignStaffTerritory}
				open={!!assignStaffTerritory}
				onClose={() => {
					setAssignStaffTerritory(null);
				}}
			/>
			<TerritoryFormModal
				open={isCreateOpen || !!editingTerritory}
				onClose={() => {
					setIsCreateOpen(false);
					setEditingTerritory(null);
				}}
				organizationId={organizationId}
				territory={editingTerritory}
				defaultParentId={currentParentId}
			/>
		</>
	);
}
